import type { ChatMessage, Conversation } from '@lemontaigu/chat';
import type { ThreadSummary } from './data/receptionStore.js';

// Pure dashboard logic — no React, no store, unit-tested in logic.test.ts.

/** Reception reads French first: the FR translation when present, else the original. */
export function receptionText(message: ChatMessage): string {
  return message.bodyFr ?? message.body;
}

/** Open threads first, then most recent activity (latest message id, else creation date). */
export function sortThreads(threads: ThreadSummary[]): ThreadSummary[] {
  return [...threads].sort((a, b) => {
    if (a.conversation.status !== b.conversation.status) {
      return a.conversation.status === 'open' ? -1 : 1;
    }
    const aKey = a.lastMessage?.id ?? 0;
    const bKey = b.lastMessage?.id ?? 0;
    if (aKey !== bKey) return bKey - aKey;
    return a.conversation.createdAt < b.conversation.createdAt ? 1 : -1;
  });
}

/**
 * Fold a live insert into the thread list. Returns null when the message
 * belongs to an unknown conversation (a new thread — caller should reload).
 */
export function applyIncomingMessage(
  threads: ThreadSummary[],
  message: ChatMessage,
): ThreadSummary[] | null {
  const index = threads.findIndex((t) => t.conversation.id === message.conversationId);
  if (index === -1) return null;
  const updated = threads.map((t, i) => (i === index ? { ...t, lastMessage: message } : t));
  return sortThreads(updated);
}

/** Unread = the latest message is from the guest and newer than what staff last saw. */
export function hasUnread(thread: ThreadSummary, lastSeenId: number | undefined): boolean {
  const last = thread.lastMessage;
  if (!last || last.sender !== 'guest') return false;
  return last.id > (lastSeenId ?? 0);
}

const LAST_SEEN_KEY = 'reception-last-seen';

export function loadLastSeen(storage: Pick<Storage, 'getItem'>): Record<string, number> {
  try {
    const parsed = JSON.parse(storage.getItem(LAST_SEEN_KEY) ?? '{}') as Record<string, number>;
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export function saveLastSeen(
  storage: Pick<Storage, 'setItem'>,
  map: Record<string, number>,
): void {
  storage.setItem(LAST_SEEN_KEY, JSON.stringify(map));
}

// ── History browse (Chambre/date search) ────────────────────────────────────
// UTC calendar days throughout — same simplification as the Atout France
// evidence query (RUNBOOK §16.4): no local-timezone conversion, so the
// boundary is exact and fully deterministic (no environment-dependent Date
// arithmetic), at the cost of a possible 1-2h offset from the staff's actual
// local midnight. Fine for a review tool; not a legal cutoff.

/** `<input type="date">` values (YYYY-MM-DD) → an inclusive UTC-day query range. */
export function dayRangeToIso(dateFrom: string, dateTo: string): { fromIso: string; toIso: string } {
  return { fromIso: `${dateFrom}T00:00:00.000Z`, toIso: `${dateTo}T23:59:59.999Z` };
}

/** Room-filtered history view: flat, most recent first. */
export function sortHistoryByDate(conversations: Conversation[]): Conversation[] {
  return [...conversations].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

export interface HistoryDateGroup {
  /** UTC calendar day (YYYY-MM-DD) of `createdAt`. */
  date: string;
  /** Sorted by room number ascending. */
  conversations: Conversation[];
}

/** All-rooms history view: grouped by day (most recent first), rooms ascending within a day. */
export function groupHistoryByDate(conversations: Conversation[]): HistoryDateGroup[] {
  const byDate = new Map<string, Conversation[]>();
  for (const c of conversations) {
    const date = c.createdAt.slice(0, 10);
    const list = byDate.get(date);
    if (list) list.push(c);
    else byDate.set(date, [c]);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([date, convs]) => ({
      date,
      conversations: [...convs].sort((a, b) => Number(a.room) - Number(b.room)),
    }));
}
